import React, { useEffect, useState, useContext, useMemo, useCallback, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import Axios from "axios";
import { safeJwtDecode } from "../../utilities/tokenUtils";
import DOMPurify from "dompurify";
import toast from "react-hot-toast";
import UserContext from "../../context/UserContext";

export default function UserFilter() {
  const { userData } = useContext(UserContext);
  const navigate = useNavigate();

  const [users, setUsers] = useState([]);
  const [keyword, setKeyword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const hasFetchedRef = useRef(false);

  useEffect(() => {
    if (!userData.token) {
      navigate("/admin/login");
    } else if (safeJwtDecode(userData.token).role !== "administrador") {
      navigate("/");
    }
  }, [navigate, userData.token]);

  const decoded = safeJwtDecode(userData.token);
  const userRole = decoded.role ?? null;  
  const userDisplayName = decoded.displayName ?? null;

  const fetchRegisteredUsers = useCallback(async () => {
    setIsLoading(true);
    try {
      const { data } = await Axios.get(
        `${process.env.REACT_APP_BACK_DOMAIN}/users/list`,
        { headers: { "x-auth-token": userData.token } }
      );
      setUsers(data);
    } catch (err) {
      toast.error("Error al obtener usuarios");
    } finally {
      setIsLoading(false);
    }
  }, [userData.token]);

  useEffect(() => {
    // Evitar ejecuciones duplicadas en React 18 StrictMode
    if (!hasFetchedRef.current && userData.token) {
      hasFetchedRef.current = true;
      fetchRegisteredUsers();
    }
  }, [fetchRegisteredUsers, userData.token]);

  const toggleUserStatus = async (id) => {
    if (!window.confirm("¿Desea cambiar el estado de este usuario?")) return;
    try {
      await Axios.put(
        `${process.env.REACT_APP_BACK_DOMAIN}/users/delete/${id}`,
        {},
        { headers: { "x-auth-token": userData.token } }
      );
      toast.success("Usuario modificado");
      fetchRegisteredUsers();
    } catch (err) {
      toast.error("Error al modificar usuario");
    }
  };

  const sanitizedKeyword = useMemo(
    () => DOMPurify.sanitize(keyword.trim().toLowerCase()),
    [keyword]
  );

  const filteredUsers = useMemo(() => {
    if (!sanitizedKeyword) return users;
    return users.filter((u) =>
      u.displayName.toLowerCase().includes(sanitizedKeyword) ||
      u.email.toLowerCase().includes(sanitizedKeyword)
    );
  }, [users, sanitizedKeyword]);

  const formatDate = (iso) =>
    iso ? new Date(iso).toLocaleString("es-CO") : "";

  return (
    <div className="page">
      <div className="container">
        <h2>Usuarios</h2>

        <input
          id="search"
          className="Form-Input"
          placeholder="Buscar por nombre…"
          value={keyword}
          onChange={(e) => setKeyword(e.target.value)}
        />
      </div>

      <br />

      {isLoading ? (
        <p>Cargando usuarios…</p>
      ) : (
        <table className="striped striped_users" id="usersReport">
          <thead>
            <tr>
              <th>#</th><th>Email</th><th>Usuario</th><th>Rol</th><th>Estado</th>
              <th>Creado</th><th>Creador</th><th>Actualizado</th><th>Modificador</th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {filteredUsers.map((u, i) =>
              userRole === "administrador" || u.displayName === userDisplayName ? (
                <tr key={u._id}>
                  <td>{i + 1}</td>
                  <td style={{ width: 200 }}>{u.email}</td>
                  <td style={{ width: 150 }}>{u.displayName}</td>
                  <td>{u.role}</td>
                  <td>{u.status ? "Activo" : "Inactivo"}</td>
                  <td>{formatDate(u.created)}</td>
                  <td>{u.creator}</td>
                  <td>{formatDate(u.updated)}</td>
                  <td>{u.modificator}</td>
                  <td style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                    {userRole === "administrador" && (
                      <button
                        className="btn"
                        onClick={() => toggleUserStatus(u._id)}
                        title="Cambiar estado"
                      >
                        <i className="material-icons notranslate">delete</i>
                      </button>
                    )}
                    <Link className="btn" to={`/admin/update?id=${u._id}`} title="Editar">
                      <i className="material-icons notranslate">settings</i>
                    </Link>
                  </td>
                </tr>
              ) : null
            )}
            {filteredUsers.length === 0 && (
              <tr>
                <td colSpan={10} style={{ textAlign: "center" }}>
                  Sin resultados
                </td>
              </tr>
            )}
          </tbody>
        </table>
      )}
    </div>
  );
}